/**
 * Web session keep-alive and "session ended" handling (security hardening batch 2,
 * 30 Sep 2026).
 *
 * The server ends a session after a period without user activity (15 minutes for
 * staff, 30 for clients by default) and after an absolute limit. Background polling
 * does not count as activity, so this module tells the server when the person is
 * actually using the page:
 *   - mouse, keyboard, touch and scroll mark activity (shared across tabs through
 *     localStorage, so one active tab keeps the others alive);
 *   - while there has been activity in the last minute, a heartbeat goes to
 *     POST /auth/heartbeat once a minute;
 *   - holdSessionAwake() keeps heartbeats going with no input at all, for a live
 *     video call, a voice session or an assessment in progress.
 *
 * When the server answers 401 with a session code, a window event
 * 'aimhi:session-ended' is dispatched and the app returns to the login page.
 */
import axios from 'axios';

const ACTIVITY_KEY = 'aimhi:lastActivity';
const NOTICE_KEY = 'aimhi:sessionEndedNotice';
const HEARTBEAT_EVERY_MS = 60 * 1000;
const SESSION_CODES = new Set([
  'SESSION_EXPIRED', 'SESSION_REVOKED', 'SESSION_INVALID', 'SESSION_REQUIRED',
  'ACCOUNT_INACTIVE', 'USER_NOT_FOUND', 'TOKEN_INVALID'
]);

const holds = new Set();
let lastActivityLocal = Date.now();
let lastHeartbeatAt = 0;
let idleTimeoutMinutes = null;
let heartbeatTimer = null;
let apiBase = '';
let getToken = () => null;
let started = false;
let endedDispatched = false;

function now() { return Date.now(); }

export function lastActivity() {
  let shared = 0;
  try { shared = Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch (_) { /* private mode */ }
  return Math.max(lastActivityLocal, shared);
}

function markActivity() {
  const t = now();
  // Writing on every mousemove is wasteful; once every few seconds is plenty.
  if (t - lastActivityLocal < 5000) return;
  lastActivityLocal = t;
  try { localStorage.setItem(ACTIVITY_KEY, String(t)); } catch (_) { /* private mode */ }
}

export function recordActivity() {
  lastActivityLocal = 0; // force the write below
  markActivity();
}

export function getIdleTimeoutMinutes() { return idleTimeoutMinutes; }

/** Keep the session alive without input (live call, voice, assessment). Returns a release function. */
export function holdSessionAwake(reason = 'hold') {
  const key = `${reason}:${Math.random().toString(36).slice(2)}`;
  holds.add(key);
  return () => holds.delete(key);
}

export function hasHolds() { return holds.size > 0; }

export async function sendHeartbeat() {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await axios.post(`${apiBase}/auth/heartbeat`, {}, { headers: { 'x-auth-token': token, 'x-user-activity': '1' } });
    lastHeartbeatAt = now();
    if (res.data && res.data.idleTimeoutMinutes) idleTimeoutMinutes = res.data.idleTimeoutMinutes;
    return res.data;
  } catch (err) {
    // A 401 is handled by the interceptor; anything else is retried next tick.
    return null;
  }
}

function tick() {
  if (!getToken()) return;
  const recentlyActive = now() - lastActivity() < HEARTBEAT_EVERY_MS;
  if ((recentlyActive || holds.size > 0) && now() - lastHeartbeatAt >= HEARTBEAT_EVERY_MS - 1000) {
    sendHeartbeat();
  }
}

export function dispatchSessionEnded(code) {
  if (endedDispatched) return;
  endedDispatched = true;
  try {
    sessionStorage.setItem(NOTICE_KEY, code === 'SESSION_EXPIRED'
      ? 'You were signed out after a period of inactivity, to keep your information safe. Please sign in again.'
      : 'Your session has ended. Please sign in again.');
  } catch (_) { /* private mode */ }
  window.dispatchEvent(new CustomEvent('aimhi:session-ended', { detail: { code } }));
}

/** Read (once) the message the login page shows after a session ended. */
export function takeSessionEndedNotice() {
  try {
    const msg = sessionStorage.getItem(NOTICE_KEY);
    if (msg) sessionStorage.removeItem(NOTICE_KEY);
    return msg;
  } catch (_) {
    return null;
  }
}

function interceptor(error) {
  const status = error && error.response && error.response.status;
  const code = error && error.response && error.response.data && error.response.data.code;
  if (status === 401 && SESSION_CODES.has(code) && getToken()) {
    dispatchSessionEnded(code);
  }
  // The account must change its password before doing anything else (security batch 3).
  if (status === 403 && code === 'PASSWORD_CHANGE_REQUIRED' && getToken()) {
    window.dispatchEvent(new CustomEvent('aimhi:password-change-required'));
  }
  // Staff role requires two-step sign-in and it is not set up yet (security batch 4).
  if (status === 403 && code === 'MFA_ENROLLMENT_REQUIRED' && getToken()) {
    window.dispatchEvent(new CustomEvent('aimhi:mfa-enrollment-required'));
  }
  return Promise.reject(error);
}

/**
 * Install once at start-up. Covers the shared axios default instance (used directly by
 * many components) and any extra instances passed in.
 */
export function installSessionHandling({ baseUrl, tokenGetter, instances = [] }) {
  if (started) return;
  started = true;
  apiBase = baseUrl;
  getToken = tokenGetter;
  axios.interceptors.response.use(r => r, interceptor);
  instances.forEach(inst => inst.interceptors.response.use(r => r, interceptor));
  ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'].forEach(evt =>
    window.addEventListener(evt, markActivity, { passive: true, capture: true }));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) markActivity(); });
  heartbeatTimer = setInterval(tick, 15 * 1000);
}

/** Called after a fresh login so a later expiry is announced again. */
export function resetSessionState() {
  endedDispatched = false;
  lastHeartbeatAt = 0;
  idleTimeoutMinutes = null;
  recordActivity();
}

export const __testing__ = { holds, SESSION_CODES, heartbeatTimer: () => heartbeatTimer };
